import PolicyLayout, { Section } from "@/components/PolicyLayout";
import { BUSINESS, addressOneLine } from "@/data/business";

export default function PrivacyPolicy() {
  const lastUpdated = "February 1, 2026";
  return (
    <PolicyLayout
      eyebrow="Privacy"
      title="Privacy Policy"
      intro="What we collect, why we collect it, and how we keep it safe. Plain English, no dark patterns."
      testId="privacy-policy-page"
    >
      <p className="text-sm text-muted-foreground -mt-4">Last updated: {lastUpdated}</p>

      <Section title="Who we are">
        <p>
          {BUSINESS.legalName} ("we", "us", "our") operates the website {BUSINESS.url} (the "Site").
          You can reach us at{" "}
          <a href={`mailto:${BUSINESS.email.privacy}`} className="text-primary font-semibold">{BUSINESS.email.privacy}</a>{" "}
          or by post at: <em>{addressOneLine()}</em>.
        </p>
      </Section>

      <Section title="What we collect">
        <ul className="list-disc list-outside pl-5 space-y-2 marker:text-primary">
          <li><strong>Order data:</strong> name, shipping address, phone, email, items purchased, total paid.</li>
          <li><strong>Payment data:</strong> processed entirely by Stripe — we never see or store your card number. We only see a confirmation that payment succeeded.</li>
          <li><strong>Analytics:</strong> anonymous usage data via Google Analytics / Google Ads (pages visited, device type, approximate location, referral source).</li>
          <li><strong>Cookies:</strong> small files stored in your browser for session, cart, and analytics purposes. You can block these in your browser settings; some site features may stop working.</li>
        </ul>
      </Section>

      <Section title="Why we collect it">
        <ul className="list-disc list-outside pl-5 space-y-2 marker:text-primary">
          <li>Fulfilling and shipping your order (the main reason).</li>
          <li>Sending order confirmation, shipping, and (rarely) account-related emails.</li>
          <li>Preventing fraud and abuse.</li>
          <li>Measuring marketing effectiveness so we can show fewer, better ads.</li>
          <li>Improving the website experience.</li>
        </ul>
      </Section>

      <Section title="Who we share it with">
        <p>We only share data with third parties that help us run the business:</p>
        <ul className="list-disc list-outside pl-5 space-y-2 marker:text-primary">
          <li><strong>Stripe</strong> — payment processing (PCI-compliant, never sees your data we keep)</li>
          <li><strong>Shipping carriers</strong> (USPS, Canada Post, etc.) — only the shipping address and your name</li>
          <li><strong>Google Analytics / Google Ads</strong> — aggregated, anonymous traffic data; hashed email for conversion matching only</li>
          <li><strong>Hosting providers</strong> — server logs strictly for operational and security purposes</li>
        </ul>
        <p>We never sell your personal data. Ever.</p>
      </Section>

      <Section title="How long we keep it">
        <p>
          Order records: 7 years (required for tax/accounting). Analytics data: 14 months (Google
          Analytics default). Marketing email opt-ins: until you unsubscribe.
        </p>
      </Section>

      <Section title="Your rights">
        <p>You can ask us to:</p>
        <ul className="list-disc list-outside pl-5 space-y-2 marker:text-primary">
          <li>Access a copy of the data we hold about you</li>
          <li>Correct anything inaccurate</li>
          <li>Delete your data (where we're legally allowed — tax records have to be retained)</li>
          <li>Opt out of marketing communications</li>
          <li>Export your data in a machine-readable format (GDPR / CCPA portability)</li>
        </ul>
        <p>
          Email{" "}
          <a href={`mailto:${BUSINESS.email.privacy}`} className="text-primary font-semibold">{BUSINESS.email.privacy}</a>{" "}
          and we'll respond within 30 days. EU/UK customers: you have additional rights under GDPR
          and can complain to your local data protection authority. California customers: you have
          rights under the CCPA/CPRA — same email address, we'll get back to you within 45 days.
        </p>
      </Section>

      <Section title="Cookies & tracking">
        <p>
          We use first-party cookies for the shopping cart and admin sessions. We use Google's
          gtag.js (Google Ads conversion tracking and optionally Google Analytics) for measurement.
          We do not use cross-site advertising trackers beyond what's needed to measure ad
          performance.
        </p>
      </Section>

      <Section title="Security">
        <p>
          All traffic is encrypted via HTTPS. Payment data flows directly from your browser to
          Stripe — we never touch it. Admin access is gated behind authentication.
        </p>
      </Section>

      <Section title="Children">
        <p>
          We don't knowingly collect personal data from anyone under 13. If you believe a child has
          provided us with data, email us and we'll delete it.
        </p>
      </Section>

      <Section title="Changes to this policy">
        <p>
          We may update this page from time to time. The "Last updated" date at the top will always
          reflect the most recent change. Material changes will be announced via a banner on the
          site or by email if you have an open order.
        </p>
      </Section>
    </PolicyLayout>
  );
}
